
import { WeeklyMenu, MenuMeal, Recipe, CustomIngredient } from '@/types';
import { v4 as uuidv4 } from 'uuid';
import { globalSyncState, shouldAllowUpdate, setPriorityOperation } from './menuSyncState';

export const useMenuOperations = (
  weeklyMenu: WeeklyMenu | null,
  recipes: Recipe[] | null,
  updateMenu: (updatedMenu: WeeklyMenu) => Promise<void>,
  resetShoppingList: () => void
) => {
  
  const addRecipeToMenu = async (recipeId: string, day: string, mealType: string, portionSize: number) => {
    if (!weeklyMenu) return;
    
    // Establecer como operación prioritaria
    setPriorityOperation('recipe-add', 5000);
    
    if (globalSyncState.isMenuSyncing) {
      console.log('[MENU] Esperando a que termine la sincronización actual...');
      
      // Esperar a que termine la sincronización actual (máximo 3 segundos)
      await new Promise(resolve => {
        const checkSync = () => {
          if (!globalSyncState.isMenuSyncing) {
            resolve(true);
          } else {
            setTimeout(checkSync, 200);
          }
        };
        
        setTimeout(() => {
          resolve(false); // Timeout after 3 seconds
        }, 3000);
        
        checkSync();
      });
    }

    const newMeal: MenuMeal = {
      recipeId: recipeId,
      portionSize: portionSize
    };

    const updatedMenu = { ...weeklyMenu };

    if (updatedMenu.days && updatedMenu.days[day]) {
      if (updatedMenu.days[day][mealType]) {
        updatedMenu.days[day][mealType] = [...(updatedMenu.days[day][mealType] as MenuMeal[]), newMeal];
      } else {
        updatedMenu.days[day][mealType] = [newMeal];
      }
    } else {
      console.error('Día no válido:', day);
      return;
    }

    // After updating the menu, make sure to flag that the shopping list needs updating
    globalSyncState.shoppingListNeedsUpdate = true;
    await updateMenu(updatedMenu);
    
    // Trigger shopping list update event
    window.dispatchEvent(new CustomEvent('SHOPPING_LIST_UPDATE'));
  };

  const removeRecipeFromMenu = async (recipeId: string, day: string, mealType: string, index: number) => {
    if (!weeklyMenu) return;
    
    // Establecer como operación prioritaria
    setPriorityOperation('recipe-remove', 5000);
    
    if (globalSyncState.isMenuSyncing) {
      console.log('[MENU] Esperando a que termine la sincronización actual...');
      
      // Esperar a que termine la sincronización actual (máximo 3 segundos)
      await new Promise(resolve => {
        const checkSync = () => {
          if (!globalSyncState.isMenuSyncing) {
            resolve(true);
          } else {
            setTimeout(checkSync, 200);
          }
        };
        
        setTimeout(() => {
          resolve(false); // Timeout after 3 seconds
        }, 3000);
        
        checkSync();
      });
    }

    const updatedMenu = { ...weeklyMenu };

    if (updatedMenu.days && updatedMenu.days[day]) {
      // Verificar si es un ingrediente personalizado
      if (mealType === 'customIngredients' && updatedMenu.days[day].customIngredients) {
        const ingredientsArray = updatedMenu.days[day].customIngredients as CustomIngredient[];
        
        // Make sure the index is valid
        if (index >= 0 && index < ingredientsArray.length) {
          const updatedIngredients = [...ingredientsArray];
          updatedIngredients.splice(index, 1);
          updatedMenu.days[day].customIngredients = updatedIngredients;
          
          // After removing an ingredient, flag that the shopping list needs updating
          globalSyncState.shoppingListNeedsUpdate = true;
          await updateMenu(updatedMenu);
          
          // Trigger shopping list update event
          window.dispatchEvent(new CustomEvent('SHOPPING_LIST_UPDATE'));
          return;
        }
      }
      // Verificar si es una comida regular
      else if (updatedMenu.days[day][mealType]) {
        const mealArray = updatedMenu.days[day][mealType] as MenuMeal[];
        
        // Make sure the index is valid
        if (index >= 0 && index < mealArray.length) {
          const updatedMeals = [...mealArray];
          updatedMeals.splice(index, 1);
          updatedMenu.days[day][mealType] = updatedMeals;
          
          // After updating the menu, flag that the shopping list needs updating
          globalSyncState.shoppingListNeedsUpdate = true;
          await updateMenu(updatedMenu);
          
          // Trigger shopping list update event
          window.dispatchEvent(new CustomEvent('SHOPPING_LIST_UPDATE'));
          return;
        } else {
          console.error(`Invalid index ${index} for meal removal. Array length is ${mealArray.length}`);
        }
      } else {
        console.error('Comida no encontrada en el menú:', mealType);
      }
    } else {
      console.error('Día no válido:', day);
    }
  };

  const clearMenu = async () => {
    if (!weeklyMenu) return;
    
    // Establecer como operación prioritaria
    setPriorityOperation('clear-menu', 5000);

    const clearedMenu = {
      ...weeklyMenu,
      days: {
        monday: {},
        tuesday: {},
        wednesday: {},
        thursday: {},
        friday: {},
        saturday: {},
        sunday: {}
      }
    };

    await updateMenu(clearedMenu);
    resetShoppingList();
  };

  const addCustomIngredient = async (day: string, ingredient: any) => {
    if (!weeklyMenu) return;
    
    // Establecer como operación prioritaria
    setPriorityOperation('add-ingredient', 5000);
    
    if (globalSyncState.isMenuSyncing) {
      console.log('[MENU] Esperando a que termine la sincronización actual...');
      
      // Esperar a que termine la sincronización actual (máximo 3 segundos)
      await new Promise(resolve => {
        const checkSync = () => {
          if (!globalSyncState.isMenuSyncing) {
            resolve(true);
          } else {
            setTimeout(checkSync, 200);
          }
        };
        
        setTimeout(() => {
          resolve(false); // Timeout after 3 seconds
        }, 3000);
        
        checkSync();
      });
    }

    // Asegurarnos de que la cantidad incluya la unidad correcta
    const quantity = Number(ingredient.quantity) || 0;
    const unit = ingredient.unit || 'g';
    const displayQuantity = `${quantity}${unit}`;

    const newIngredient = {
      id: uuidv4(),
      name: ingredient.name,
      calories: ingredient.calories,
      protein: ingredient.protein,
      carbs: ingredient.carbs,
      fat: ingredient.fat,
      iron: ingredient.iron,
      calcium: ingredient.calcium,
      zinc: ingredient.zinc,
      magnesium: ingredient.magnesium,
      omega3: ingredient.omega3,
      quantity: quantity,
      unit: unit,
      displayQuantity: displayQuantity, // Aseguramos que displayQuantity se establezca correctamente
      source: 'custom'
    };

    const updatedMenu = { ...weeklyMenu };

    if (updatedMenu.days && updatedMenu.days[day]) {
      if (updatedMenu.days[day].customIngredients) {
        updatedMenu.days[day].customIngredients = [...updatedMenu.days[day].customIngredients as CustomIngredient[], newIngredient];
      } else {
        updatedMenu.days[day].customIngredients = [newIngredient];
      }
    } else {
      updatedMenu.days[day] = { customIngredients: [newIngredient] };
    }

    // After updating the menu, flag that the shopping list needs updating
    globalSyncState.shoppingListNeedsUpdate = true;
    await updateMenu(updatedMenu);
    
    // Trigger shopping list update event
    window.dispatchEvent(new CustomEvent('SHOPPING_LIST_UPDATE'));
  };
  
  const getMenuTotals = (day: string) => {
    if (!weeklyMenu || !recipes) return null;

    let calories = 0;
    let protein = 0;
    let carbs = 0;
    let fat = 0;
    let iron = 0;
    let calcium = 0;
    let zinc = 0;
    let magnesium = 0;
    let omega3 = 0;

    if (weeklyMenu.days && weeklyMenu.days[day]) {
      const dayMenu = weeklyMenu.days[day];
      
      Object.entries(dayMenu).forEach(([mealKey, mealValue]) => {
        if (mealKey !== 'customIngredients' && Array.isArray(mealValue)) {
          mealValue.forEach((meal: MenuMeal) => {
            const recipe = recipes.find((r) => r.id === meal.recipeId);
            if (recipe) {
              const portionSize = parseFloat(String(meal.portionSize)) || 1;
              calories += (parseFloat(String(recipe.calories)) || 0) * portionSize;
              protein += (parseFloat(String(recipe.protein)) || 0) * portionSize;
              carbs += (parseFloat(String(recipe.carbs)) || 0) * portionSize;
              fat += (parseFloat(String(recipe.fat)) || 0) * portionSize;
              iron += (parseFloat(String(recipe.iron)) || 0) * portionSize;
              calcium += (parseFloat(String(recipe.calcium)) || 0) * portionSize;
              zinc += (parseFloat(String(recipe.zinc)) || 0) * portionSize;
              magnesium += (parseFloat(String(recipe.magnesium)) || 0) * portionSize;
              omega3 += (parseFloat(String(recipe.omega3)) || 0) * portionSize;
            }
          });
        }
      });
      
      if (dayMenu.customIngredients && Array.isArray(dayMenu.customIngredients)) {
        dayMenu.customIngredients.forEach((ingredient: CustomIngredient) => {
          calories += parseFloat(String(ingredient.calories)) || 0;
          protein += parseFloat(String(ingredient.protein)) || 0;
          carbs += parseFloat(String(ingredient.carbs)) || 0;
          fat += parseFloat(String(ingredient.fat)) || 0;
          iron += parseFloat(String(ingredient.iron)) || 0;
          calcium += parseFloat(String(ingredient.calcium)) || 0;
          zinc += parseFloat(String(ingredient.zinc)) || 0;
          magnesium += parseFloat(String(ingredient.magnesium)) || 0;
          omega3 += parseFloat(String(ingredient.omega3)) || 0;
        });
      }
    }

    return { calories, protein, carbs, fat, iron, calcium, zinc, magnesium, omega3 };
  };

  return {
    addRecipeToMenu,
    removeRecipeFromMenu,
    clearMenu,
    addCustomIngredient,
    getMenuTotals
  };
};
